/**
 * BOOT — the handoff bus between the boot beat and the exit veil (spec §4).
 *
 * The launch beat lives at route `'/'` (NativeRoot); the signed-in handoff
 * reveals `/today` beneath a fixed cream veil (BootVeil, mounted at App level
 * so it OUTLIVES the `'/' → /today` route change — a component under `'/'`
 * would unmount before it could play). This tiny context carries the request
 * from NativeRoot up to BootVeil, plus the `docking` flag TopBar reads to
 * suppress its own mark while the traveling mark is still in flight.
 *
 * Inert on web by construction: NativeRoot only mounts in the Capacitor
 * shell, so `beginHandoff` is never called and `docking` stays false —
 * consumers (TopBar) render byte-identically to before.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { BootTier } from '../lib/boot/bootTier';

export type BootHandoff = { tier: BootTier; reduced: boolean };

type BootContextValue = {
  /** Non-null once NativeRoot hands off → BootVeil plays the exit. */
  handoff: BootHandoff | null;
  /** True only during a COLD Direction-B dock, until the mark lands (t=480).
   *  TopBar hides its own mark while this is true. */
  docking: boolean;
  /** NativeRoot calls this, then navigates to /today. */
  beginHandoff: (h: BootHandoff) => void;
  /** BootVeil owns these — it drives the dock timeline and clears itself. */
  setDocking: (v: boolean) => void;
  endHandoff: () => void;
};

const BootContext = createContext<BootContextValue | null>(null);

export function BootProvider({ children }: { children: ReactNode }): JSX.Element {
  const [handoff, setHandoff] = useState<BootHandoff | null>(null);
  const [docking, setDocking] = useState(false);

  const beginHandoff = useCallback((h: BootHandoff) => {
    setHandoff(h);
    // Set docking in the SAME batch as the route change so TopBar's very
    // first paint on /today already hides its mark — no one-frame flash of
    // the small mark before the traveling one arrives. Only the COLD
    // Direction-B dock travels; every other tier / reduced-motion just fades.
    setDocking(h.tier === 'COLD' && !h.reduced);
  }, []);
  const endHandoff = useCallback(() => {
    setHandoff(null);
    setDocking(false);
  }, []);

  const value = useMemo<BootContextValue>(
    () => ({ handoff, docking, beginHandoff, setDocking, endHandoff }),
    [handoff, docking, beginHandoff, endHandoff],
  );

  return <BootContext.Provider value={value}>{children}</BootContext.Provider>;
}

/** Consume the boot bus. Safe outside a provider (returns an inert stub) so
 *  components that render on the web — TopBar — never need the provider. */
export function useBoot(): BootContextValue {
  return (
    useContext(BootContext) ?? {
      handoff: null,
      docking: false,
      beginHandoff: () => {},
      setDocking: () => {},
      endHandoff: () => {},
    }
  );
}
